import { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { MapPin } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function LocationMarker({ position, setPosition, onLocationSelect }) {
  useMapEvents({
    click(e) {
      const pos = [e.latlng.lat, e.latlng.lng];

      setPosition(pos);
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return position ? <Marker position={position} /> : null;
}

function ChangeMapView({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 19, {
        animate: true,
      });
    }
  }, [center, map]);

  return null;
}

function FixMapSize() {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [map]);

  return null;
}

export default function MapPicker({
  latitude,
  longitude,
  onLocationSelect,
  buttonLocation,
  buttonGotoMap,
}) {
  const [position, setPosition] = useState([0, 0]);
  const [loadingLocation, setLoadingLocation] = useState(true);

  // Get current user location
  useEffect(() => {
    if (latitude && longitude) {
      setPosition([parseFloat(latitude), parseFloat(longitude)]);
      setLoadingLocation(false);
      return;
    }

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const current = [pos.coords.latitude, pos.coords.longitude];

          setPosition(current);

          onLocationSelect(pos.coords.latitude, pos.coords.longitude);

          setLoadingLocation(false);
        },
        () => {
          setLoadingLocation(false);
        },
        {
          enableHighAccuracy: true,
        },
      );
    } else {
      setLoadingLocation(false);
    }
  }, []);

  const resetLocation = () => {
    navigator.geolocation.getCurrentPosition((pos) => {
      const current = [pos.coords.latitude, pos.coords.longitude];

      setPosition(current);

      onLocationSelect(pos.coords.latitude, pos.coords.longitude);
    });
  };

  const gotoLocation = () => {
    if (!position || position.length !== 2) return;

    const [lat, lng] = position;

    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  if (loadingLocation) {
    return (
      <div className="h-[300px] flex items-center justify-center border rounded-lg">
        Getting your location...
      </div>
    );
  }

  return (
    <div>
      <MapContainer
        center={position}
        zoom={18}
        maxZoom={19}
        scrollWheelZoom
        style={{
          height: "250px",
          width: "100%",
        }}
        className="rounded-lg"
      >
        <FixMapSize />
        <ChangeMapView center={position} />

        <TileLayer
          maxZoom={19}
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // attribution="&copy; OpenStreetMap contributors"
        />

        <LocationMarker
          position={position}
          setPosition={setPosition}
          onLocationSelect={onLocationSelect}
        />
      </MapContainer>

      <div className="mt-3 flex gap-3">
        {buttonLocation && (
          <button
            type="button"
            onClick={resetLocation}
            className="flex items-center justify-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            📍 <span className="ml-2">Use Current Location</span>
          </button>
        )}
        {buttonGotoMap && (
          <button
            type="button"
            onClick={gotoLocation}
            className="flex items-center justify-center px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700"
          >
            <MapPin className="w-4 h-4 mr-2" />
            <span>Goto Map</span>
          </button>
        )}
      </div>
    </div>
  );
}
